<template>
  <div
    class="mega-nav__custom mega-nav grid container"
    @mouseleave="handleExit"
  >
    <div class="mega-nav__links col l6">
      <ul
        v-for="link in links"
        :key="`mega-nav-${link.handle}`"
        class="mega-nav__link-group"
      >
        <li
          class="mega-nav__link-title h6"
          :class="{
            [`mega-nav__link--${link.options?.color}`]: link.options?.color
          }"
        >
          <a
            :href="link.url.includes('visualizer') ? link.url + '/headboard' : link.url"
            :target="getTargetForUrl(link.url)"
          >
            {{ link.title }}
          </a>
        </li>

        <li
          v-for="sublink in link.links"
          :key="`mega-nav-${sublink.handle}`"
          class="mega-nav__link"
          :class="{
            [`mega-nav__link--${sublink.options?.color}`]: sublink.options?.color
          }"
        >
          <a
            :href="sublink.url.includes('visualizer') ? sublink.url + '/headboard' : sublink.url"
            class="body-2 mega-nav__navigation-link"
            :target="getTargetForUrl(sublink.url)"
          >
            {{ sublink.title }}
          </a>
        </li>
      </ul>
    </div>

    <!-- Featured Navigation -->
    <div
      v-if="featured.length"
      class="col l2 mega-nav__featured"
    >
      <h6
        class="mega-nav__featured-title"
        :class="{
          [`mega-nav__link--${featured[0].options?.color}`]: featured[0].options?.color
        }"
      >
        {{ featured[0].title }}
      </h6>
      <ul>
        <li
          v-for="(link, i) in featured[0].links"
          :key="`mega-nav-${link.handle}`"
          class="mega-nav__link"
          :class="{
            [`mega-nav__link--${link.options?.color}`]: link.options?.color
          }"
          @mouseenter="handleIconHover($event, link, navIcons[i])"
          @mouseleave="handleIconHover($event, link, navIcons[i])"
        >
          <a
            :href="link.url.includes('visualizer') ? link.url + '/headboard' : link.url"
            class="body-2 mega-nav__navigation-link"
            :target="getTargetForUrl(link.url)"
          >
            <span
              v-if="link.options?.image"
              class="icon"
            >
              <img
                :ref="el => navIcons[i] = el"
                :alt="link.title + ' ' + $string('accessibility.icon')"
                :src="getIcon(link)"
              >
            </span>
            {{ link.title }}
          </a>
        </li>
      </ul>
    </div>
    <div class="col l9-12 mega-nav__banners">
      <header-banner
        v-for="(banner, bannerIndex) in banners"
        :key="`banner-${index}-${bannerIndex}`"
        :settings="banner"
        block-class="mega-nav"
        image-ratio="448:257"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Mega nav (mega-nav)
 * -----------------------------------------------------------------------------
 * A large navigation for desktop that displays sublinks to tier 1.
 *
 * @param {Array} banners - Banner cards with an image and text overlay to
 * display on the right side of the mega nav.
 * @param {Number} index - The index of the tier 1 item that is being
 * clicked. Used for the unique banner images.
 * to open this mega nav (0 based).
 * @param {Boolean} isActive - Is overlay active?
 * @param {Object} menu - The menu linklist object.
 */

import { mapActions, mapState } from 'vuex'
import HeaderBanner from '~async/header-banner/header-banner'
import { getIcon, handleIconHover } from '~/helpers/general'

export default {
  name: 'MegaNav',
  components: { HeaderBanner },
  props: {
    banners: {
      type: Array,
      default: () => ([]),
    },
    index: {
      type: Number,
      default: 0,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    menu: {
      type: Object,
      default: () => ({
        links: [],
      }),
    },
    resetScroll: {
      type: Function,
      default() {
        return {}
      },
    },
    scrollElement: {
      type: HTMLElement,
      default: null,
    },
  },

  data() {
    return {
      getIcon,
      handleIconHover,
      global: true,
      navIcons: [],
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    featured() {
      if (!this.menu) {
        return []
      }

      return this.menu.links.filter(link => {
        return link.title.toLowerCase().includes('featured')
      })
    },

    links() {
      if (!this.menu) {
        return []
      }

      return this.menu.links.filter(link => {
        return !link.title.toLowerCase().includes('featured')
      })
    },
  },

  watch: {

    /**
     * Watch isMobile to close on change.
     */
    isMobile() {
      if (this.isMobile) {
        return
      }

      this.closeOverlay('megaNav')
    },

    menu() {
      this.resetScroll()
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
    }),

    handleExit(event) {
      if (
        event.offsetY > 0 &&
        event.offsetX > 0 &&
        event.offsetX < this.scrollElement.clientWidth
      ) {
        this.closeOverlay('megaNav')
      }
    },

    getTargetForUrl(url) {
      try {
        const urlObj = new URL(url)
        if (urlObj.hostname.length > 0 && !urlObj.hostname.includes('.dormify.com')) {
          return '_blank'
        }
      } catch (e) {
        console.error('error in getTargetForUrl for url', url)
      }
      return '_self'
    },
  },
}
</script>
